import pendingItemDialog from './PendingItemDialog.html';

/*******************************************************************************
 * @ngdoc    service
 * @name     csm.books.InitialSetup.dialogs.service:PendingItemDialog
 * @description
 * A modal dialog for add/change of bank account outstanding check/deposit items.
 *******************************************************************************/
/* @ngInject */ export function PendingItemDialogService($injector) {
  this.show = $injector.invoke(show);
};

/*******************************************************************************
 * show */
/**
 * @ngdoc    function
 * @name     show
 * @methodOf csm.books.InitialSetup.dialogs.service:PendingItemDialog
 * @param    {OutstandingBankItem} itemInfo     Pending item information.
 * @param    {boolean}             isNewItem    `true`=New item.
 * @return   {promise}                          Resolves with `itemInfo`.
 * @description
 * Shows the dialog.
 *******************************************************************************/
/* @ngInject */ function show($uibModal, $rootScope) {
  return function (itemInfo, isNewItem) {
    return $uibModal.open({
      template: pendingItemDialog,
      backdrop: 'static',
      controller: controller,
      controllerAs: '$ctrl',
      bindToController: true,
      scope: Object.assign($rootScope.$new(true), {
        itemInfo: itemInfo,
        isNewItem: isNewItem
      })
    }).result;
  };
}

/*******************************************************************************
 * controller
 *******************************************************************************/
/* @ngInject */ function controller($injector, $scope, $uibModalInstance) {
  var $svcs = {
    $ctrl: this,
    $scope: $scope,
    $uibModalInstance: $uibModalInstance
  };
  var $ctrl = $injector.invoke(
    init$ctrl,
    null,
    Object.assign($svcs, { $svcs: $svcs })
  );

  /*---------------------------------------------------*/
  /* Set `$ctrl.modified` when user changes item info. */
  /*---------------------------------------------------*/
  var unwatch = $scope.$watch(
    '$ctrl.itemInfo',
    function (nv, ov) {
      if (nv !== ov) unwatch(($ctrl.modified = true));
    },
    true
  );

  $scope.$on('destroy', unwatch);
}

/*****************************************************************************
 * init$ctrl */
/**
 * $ctrl property initializer.
 *
 * @param  $svcs  Properties to pass with `$injector.invoke()`.
 *****************************************************************************/
/* @ngInject */ function init$ctrl($svcs, $ctrl, $injector, AppData) {
  /*------------*/
  /* Properties */
  /*------------*/
  $ctrl.modified = false; // Set when `$ctrl.itemInfo` is changed by user.
  $ctrl.validators = {}; // `csm-validators` control.

  /*----------*/
  /* Handlers */
  /*----------*/
  $ctrl.cancel = $injector.invoke(cancel, null, $svcs);
  $ctrl.save = $injector.invoke(save, null, $svcs);

  /*------------------------*/
  /* Labels and validators. */
  /*------------------------*/
  return Object.defineProperties($ctrl, {
    /*-----------------------------------*/
    /* amountLabel - Amount field label. */
    /*-----------------------------------*/
    amountLabel: {
      get: function () {
        return (
          (!$ctrl.itemInfo.isOther ? $ctrl.itemInfo.type + ' ' : '') +
          'Amount'
        );
      }
    },

    /*-----------------------------------------------*/
    /* amountValidator - May be < 0 for Other, only. */
    /*-----------------------------------------------*/
    amountValidator: {
      value: function (v) {
        return !v || isNaN(v)
          ? 'Value is required'
          : v < 0 && !$ctrl.itemInfo.isOther
            ? 'Must be > 0'
            : null;
      }
    },

    /*------------------------------------------------------------------*/
    /* checkNumberModel - ngModel getterSetter. Returns blank for zero. */
    /*------------------------------------------------------------------*/
    checkNumberModel: {
      get: function () {
        var v = $ctrl.itemInfo.checkNumber;
        return !!v && !isNaN(v) ? v : '';
      },
      set: function (v) {
        $ctrl.itemInfo.checkNumber = v;
      }
    },

    /*-------------------------------------*/
    /* checkNumberValidator - Checks only. */
    /*-------------------------------------*/
    checkNumberValidator: {
      value: function (v) {
        return $ctrl.itemInfo.isCheck && (!v || isNaN(v))
          ? 'Value is required'
          : null;
      }
    },

    /*---------------------------------------------*/
    /* dateValidator - Can't be past opening date. */
    /*---------------------------------------------*/
    dateValidator: {
      value: function (v) {
        return !v || isNaN(v)
          ? 'Value is required'
          : v > AppData.orgInfo.openDate
            ? "Can't be after the Opening Date"
            : null;
      }
    },

    /*-----------------------*/
    /* title - Dialog title. */
    /*-----------------------*/
    title: {
      get: function () {
        var type = $ctrl.itemInfo.isOther ? 'Item' : $ctrl.itemInfo.type;
        return $ctrl.isNewItem ? 'Add New ' + type : type + ' Information';
      }
    }
  });
}

/*******************************************************************************
 * $ctrl.cancel */
/**
 * Cancel button handler. Shows confirm dialog if user changed any data. Closes
 * the dialog if user confirms or if no changes have been made.
 *******************************************************************************/
/* @ngInject */ function cancel($ctrl, $q, $uibModalInstance, Alert) {
  return function () {
    (!$ctrl.modified
      ? $q.resolve()
      : Alert.confirm('Are you sure you want to cancel?', 'Cancel')
    ).then(function () {
      $uibModalInstance.dismiss();
    });
  };
}

/*******************************************************************************
 * $ctrl.save */
/**
 * Ok button handler. Runs validators and resolves dialog with new/updated item
 * information if validators are successful.
 *******************************************************************************/
/* @ngInject */ function save($ctrl, $uibModalInstance) {
  return function () {
    $ctrl.validators.reset();
    if ($ctrl.validators.validate(true))
      $uibModalInstance.close($ctrl.itemInfo);
  };
}
